import type {
  AnalyticsEventName,
  AnalyticsEventPayloadMap,
  CmsPayload,
  GuideViewedPayload,
  PowerViewedPayload,
  SearchPayload,
  TrackEventInput,
  WorkspacePayload,
} from '@/types/analytics/events';
import { AnalyticsEventNames } from '@/types/analytics/events';
import { gaEvent, gaPageView, isGaConfigured } from './googleAnalytics';
import {
  getOrCreateSessionId,
  getOrCreateVisitorId,
  ingestAdminEvent,
  ingestPublicEvent,
} from './ingest.service';

export * from './events';
export * from './dashboard.service';
export { isGaConfigured } from './googleAnalytics';
export { getOrCreateSessionId, getOrCreateVisitorId } from './ingest.service';

type TrackOptions = {
  /** Envia também via rota admin autenticada (CMS). */
  admin?: boolean;
  sessionId?: string;
  visitorId?: string;
};

function deviceHint(): string {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/Tablet|iPad/i.test(ua)) return 'tablet';
  if (/Mobi|Android/i.test(ua)) return 'mobile';
  return 'desktop';
}

function enrichPayload(payload?: Record<string, unknown>): Record<string, unknown> {
  const path = typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined;
  const referrer = typeof document !== 'undefined' ? document.referrer || undefined : undefined;
  return {
    path,
    referrer,
    device: deviceHint(),
    channel: referrer ? 'Referência' : 'Direto',
    ...payload,
  };
}

async function dispatch(
  event: AnalyticsEventName | string,
  payload?: Record<string, unknown>,
  options?: TrackOptions
): Promise<void> {
  const body = {
    event_type: event,
    payload: enrichPayload(payload),
    session_id: options?.sessionId ?? getOrCreateSessionId(),
    visitor_id: options?.visitorId ?? getOrCreateVisitorId(),
  };

  gaEvent(event, body.payload);

  if (options?.admin) {
    await ingestAdminEvent(body);
  } else {
    await ingestPublicEvent(body);
  }
}

export const analytics = {
  track<T extends AnalyticsEventName>(input: TrackEventInput<T>, options?: TrackOptions) {
    return dispatch(input.event, input.payload as Record<string, unknown> | undefined, {
      ...options,
      sessionId: input.sessionId ?? options?.sessionId,
      visitorId: input.visitorId ?? options?.visitorId,
    });
  },

  pageView(path?: string, title?: string) {
    const pagePath =
      path ?? (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/');
    gaPageView(pagePath, title);
    return dispatch(AnalyticsEventNames.PAGE_VIEW, { path: pagePath, title });
  },

  event(name: AnalyticsEventName | string, payload?: Record<string, unknown>, options?: TrackOptions) {
    return dispatch(name, payload, options);
  },

  guideViewed(payload: GuideViewedPayload, options?: TrackOptions) {
    return dispatch(AnalyticsEventNames.GUIDE_VIEWED, payload as Record<string, unknown>, options);
  },

  powerViewed(payload: PowerViewedPayload, options?: TrackOptions) {
    return dispatch(AnalyticsEventNames.POWER_VIEWED, payload as Record<string, unknown>, options);
  },

  powerOpen(payload: PowerViewedPayload, options?: TrackOptions) {
    return dispatch(AnalyticsEventNames.POWER_OPEN, {
      ...payload,
      action: 'open_power',
    } as Record<string, unknown>, options);
  },

  powerViewSmilies(payload: PowerViewedPayload, options?: TrackOptions) {
    return dispatch(AnalyticsEventNames.POWER_VIEW_SMILIES, {
      ...payload,
      action: 'view_smilies',
    } as Record<string, unknown>, options);
  },

  search(payload: SearchPayload, options?: TrackOptions) {
    const zero = (payload.resultCount ?? 0) === 0;
    return dispatch(
      zero ? AnalyticsEventNames.SEARCH_WITHOUT_RESULTS : AnalyticsEventNames.SEARCH_PERFORMED,
      payload as Record<string, unknown>,
      options
    );
  },

  workspace(
    event:
      | typeof AnalyticsEventNames.WORKSPACE_CARD_CREATED
      | typeof AnalyticsEventNames.WORKSPACE_CARD_COMPLETED
      | typeof AnalyticsEventNames.WORKSPACE_COMMENT_ADDED,
    payload?: WorkspacePayload,
    options?: TrackOptions
  ) {
    return dispatch(event, payload as Record<string, unknown>, options);
  },

  cms(
    event:
      | typeof AnalyticsEventNames.CMS_LOGIN
      | typeof AnalyticsEventNames.CMS_LOGOUT
      | typeof AnalyticsEventNames.CMS_PUBLISH
      | typeof AnalyticsEventNames.CMS_DELETE
      | typeof AnalyticsEventNames.GUIDE_UPDATED,
    payload?: CmsPayload,
    options?: TrackOptions
  ) {
    return dispatch(event, payload as Record<string, unknown>, { ...options, admin: true });
  },
};

export type { AnalyticsEventPayloadMap };
